<script setup>
// Persistent banner for staff whose install is not a reachable push device
// (#149, notifications-v2 only). Keys on this install; when the staff member
// is reachable on another device the wording is softer and it can be put away
// for the session. Goes away once this install is a reachable push device.
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { IconBellOff } from '@tabler/icons-vue'
import PushSetupSteps from '@/components/PushSetupSteps.vue'
import { usePushReachabilityStore } from '@/stores/pushReachability.js'
import { PUSH_SETUP_ROUTE } from '@/services/reachability.js'

const reach = usePushReachabilityStore()
const route = useRoute()
const open = ref(false)
const d = __DICT__.notifications

// Not on login / PIN, nor on the block screen, which shows the same steps.
const HIDDEN_ON = new Set(['Login', 'Unlock', PUSH_SETUP_ROUTE])

const banner = computed(() => reach.banner)
const visible = computed(() => {
  if (!banner.value || reach.blocked || HIDDEN_ON.has(route.name)) return false
  return !(banner.value.reachableElsewhere && reach.dismissed)
})
</script>

<template>
  <div v-if="visible" class="push-banner alert alert-warning mb-0 rounded-0" role="status">
    <div class="container-xl">
      <div class="d-flex align-items-start">
        <IconBellOff class="me-2 flex-shrink-0" :size="20" />
        <div class="flex-grow-1">
          <div class="fw-bold">{{ banner.reachableElsewhere ? d.bannerTitleElsewhere : d.bannerTitle }}</div>
          <div class="text-secondary">{{ banner.reachableElsewhere ? d.bannerBodyElsewhere : d.bannerBody }}</div>
          <div class="d-flex gap-2 mt-2">
            <button class="btn btn-sm btn-warning" @click="open = !open">{{ open ? d.hideSteps : d.showSteps }}</button>
            <button v-if="banner.reachableElsewhere" class="btn btn-sm btn-ghost-secondary" @click="reach.dismissed = true">
              {{ d.notNow }}
            </button>
          </div>
          <PushSetupSteps
              v-if="open"
              class="mt-3"
              :kind="banner.kind"
              :open-in-safari="reach.install.openInSafari"
              @enabled="reach.refresh()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.push-banner {
  font-size: 0.875rem;
  border-left: 0;
  border-right: 0;
  border-top: 0;
}
</style>
